import KeenSlider, { KeenSliderInstance } from "keen-slider";
import { sliderAnimation } from "helpers/sliderConfig";

interface CardSlideElement extends HTMLElement {
    _keenSlider?: KeenSliderInstance;
    _listenersAdded?: boolean;
    _recreateSlider?: () => void;
}

(function (): void {
    const sliders = document.querySelectorAll<CardSlideElement>(".js-card-slide");
    if (sliders.length === 0) return;

    const getPerView = (slider: CardSlideElement): number => {
        const w = window.innerWidth;

        // Case studies: always 1
        if (slider.parentElement?.classList.contains("js-case-studies-block")) {
            return 1;
        }

        // Person listing block: 4 -> 3 -> 2 -> 1
        if (slider.closest(".js-person-listing-block")) {
            if (w >= 1280) return 4;
            if (w >= 1024) return 3;
            if (w >= 768) return 2;
            return 1;
        }

        // Testimonials block: cap columns at actual slide count so 2 slides fill 50% each on lg
        if (slider.closest(".js-testimonials-block")) {
            const slideCount = slider.querySelectorAll(".slide").length;
            if (w >= 1024) return Math.min(3, slideCount);
            if (w >= 768) return Math.min(2, slideCount);
            return 1;
        }

        // Default: 3 -> 2 -> 1
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };

    const updateButtons = (
        keenInstance: KeenSliderInstance,
        prevBtn: HTMLButtonElement | null,
        nextBtn: HTMLButtonElement | null,
        container: HTMLElement | null
    ): void => {
        if (!prevBtn || !nextBtn) return;
        const { rel, maxIdx } = keenInstance.track.details;

        if (rel <= 0) {
            prevBtn.setAttribute("disabled", "");
        } else {
            prevBtn.removeAttribute("disabled");
        }

        if (rel >= maxIdx) {
            nextBtn.setAttribute("disabled", "");
        } else {
            nextBtn.removeAttribute("disabled");
        }

        if (container) {
            const bothDisabled = prevBtn.hasAttribute("disabled") && nextBtn.hasAttribute("disabled");
            container.style.display = bothDisabled ? "none" : "";
        }
    };

    const isPillarsBlock = (slider: CardSlideElement): boolean =>
        !!slider.closest(".global-pillars");

    const isBundlingBlock = (slider: CardSlideElement): boolean =>
        !!slider.closest(".bundling-block");

    const destroySlider = (slider: CardSlideElement): void => {
        const instance = slider._keenSlider;
        if (!instance) return;
        instance.destroy();
        delete slider._keenSlider;
        // Remove keen-slider classes and inline styles so unslide CSS takes full control
        const track = slider.querySelector<HTMLElement>(".slider__track");
        if (track) {
            track.classList.remove("keen-slider");
            track.style.removeProperty("transform");
            track.querySelectorAll<HTMLElement>(".slide").forEach((slide) => {
                slide.classList.remove("keen-slider__slide");
                slide.style.removeProperty("min-width");
                slide.style.removeProperty("max-width");
                slide.style.removeProperty("width");
            });
        }
    };

    const applyUnslide = (slider: CardSlideElement): void => {
        destroySlider(slider);
        slider.classList.add("unslide");
        const btnContainer = slider.querySelector<HTMLElement>(".card__button__container");
        if (btnContainer) btnContainer.style.display = "none";
    };

    const removeUnslide = (slider: CardSlideElement): void => {
        slider.classList.remove("unslide");
        const btnContainer = slider.querySelector<HTMLElement>(".card__button__container");
        if (btnContainer) btnContainer.style.display = "";
    };

    const initSlider = (slider: CardSlideElement): void => {
        // PillarsBlock: no slider below lg (1024px)
        if (isPillarsBlock(slider) && window.innerWidth < 1024) {
            applyUnslide(slider);
            return;
        }

        // BundlingBlock: no slider below lg (768px)
        if (isBundlingBlock(slider) && window.innerWidth < 768) {
            applyUnslide(slider);
            return;
        }

        // Skip: grid/single-item layouts
        if (slider.classList.contains("unslide")) return;

        // Skip: first related content (grid layout wrapper)
        if (slider.parentElement?.parentElement?.classList.contains("first__related__content")) return;

        // Skip: testimonial (has its own slider)
        if (slider.closest(".js-testimonial-slider-block")) return;

        const track = slider.querySelector<HTMLElement>(".slider__track");
        if (!track) return;

        const slides = Array.from(track.querySelectorAll<HTMLElement>(".slide"));
        if (slides.length === 0) return;

        track.classList.add("keen-slider");
        slides.forEach((slide) => slide.classList.add("keen-slider__slide"));

        const prevBtn = slider.querySelector<HTMLButtonElement>(".card__prev");
        const nextBtn = slider.querySelector<HTMLButtonElement>(".card__next");
        const btnContainer = slider.querySelector<HTMLElement>(".card__button__container");

        const createInstance = (): void => {
            const currentSlide = slider._keenSlider?.track.details.rel ?? 0;
            slider._keenSlider?.destroy();
            slider._keenSlider = new KeenSlider(track, {
                mode: "snap",
                slides: { perView: getPerView(slider), spacing: 24 },
                defaultAnimation: sliderAnimation,
                initial: currentSlide,
                created(s) { updateButtons(s, prevBtn, nextBtn, btnContainer); },
                slideChanged(s) { updateButtons(s, prevBtn, nextBtn, btnContainer); },
                updated(s) { updateButtons(s, prevBtn, nextBtn, btnContainer); },
            });
        };

        createInstance();

        if (!slider._listenersAdded) {
            prevBtn?.addEventListener("click", () => slider._keenSlider?.prev());
            nextBtn?.addEventListener("click", () => slider._keenSlider?.next());
            slider._listenersAdded = true;
        }

        slider._recreateSlider = createInstance;
    };

    sliders.forEach(initSlider);

    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            document.querySelectorAll<CardSlideElement>(".js-card-slide").forEach((slider) => {
                if (isPillarsBlock(slider)) {
                    if (window.innerWidth < 1024) {
                        // Transition to unslide if not already
                        if (!slider.classList.contains("unslide")) {
                            applyUnslide(slider);
                        }
                    } else {
                        // Transition back to slider if needed
                        if (slider.classList.contains("unslide")) {
                            removeUnslide(slider);
                            initSlider(slider);
                        } else if (slider._recreateSlider) {
                            slider._recreateSlider();
                        }
                    }
                    return;
                }
                if (slider._recreateSlider) {
                    slider._recreateSlider();
                }
            });
        }, 150);
    });
})();
